import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAlertLogs } from "../api/alerts";

const CHANNEL_COLORS = { email: "primary", sms: "success", in_app: "default" };
const STATUS_COLORS = { sent: "success", failed: "error", skipped: "warning" };

export default function AlertLogs() {
  const { t } = useTranslation("alerts");
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [channelFilter, setChannelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const loadLogs = useCallback(async () => {
    try {
      const params = { page, page_size: 25 };
      if (channelFilter) params.channel = channelFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await getAlertLogs(params);
      setLogs(data.results || []);
      setTotal(data.count || 0);
    } catch {
      /* ignore */
    }
  }, [page, channelFilter, statusFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const totalPages = Math.ceil(total / 25);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {t("logs.title")}
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t("logs.filterChannel")}</InputLabel>
          <Select
            value={channelFilter}
            label={t("logs.filterChannel")}
            onChange={(e) => {
              setChannelFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">{t("logs.all")}</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
            <MenuItem value="in_app">In-App</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t("logs.filterStatus")}</InputLabel>
          <Select
            value={statusFilter}
            label={t("logs.filterStatus")}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">{t("logs.all")}</MenuItem>
            <MenuItem value="sent">{t("logs.statuses.sent")}</MenuItem>
            <MenuItem value="failed">{t("logs.statuses.failed")}</MenuItem>
            <MenuItem value="skipped">{t("logs.statuses.skipped")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {logs.length === 0 ? (
        <Typography color="text.secondary">{t("logs.noLogs")}</Typography>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("logs.date")}</TableCell>
                <TableCell>{t("logs.recipient")}</TableCell>
                <TableCell>{t("logs.template")}</TableCell>
                <TableCell>{t("logs.channel")}</TableCell>
                <TableCell>{t("logs.status")}</TableCell>
                <TableCell>{t("logs.subject")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    {new Date(log.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {log.recipient_name || log.recipient_email}
                  </TableCell>
                  <TableCell>{log.template_name || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.channel}
                      size="small"
                      color={CHANNEL_COLORS[log.channel] || "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`logs.statuses.${log.status}`)}
                      size="small"
                      color={STATUS_COLORS[log.status] || "default"}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 300,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {log.subject}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
